import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ChangeEvent } from "react";
import workspaceIcon from "../../../assets/img/workspaceIcon.svg";
import { UserFormTextField } from "../../../components/UserFormTextField/UserFormTextField";
import { WorkspaceVisibility } from "../../../types/workspace";
import {
  countResolved,
  groupedResolved,
  groupedResolvedNames,
  groupedUnresolved,
  pluralizeSectionLabel,
  ResolverResult,
  UnresolvedSection,
} from "../../../utils/workspace/resolver";

export type OrgDefaultsPreview =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; result: ResolverResult; isEmpty: boolean }
  | { status: "error" };

interface Props {
  baseId: string;
  workspaceName: string;
  workspaceDescription: string;
  workspaceVisibility: string;
  isNameInError: boolean;
  isDescriptionInError: boolean;
  nameErrorMessage: string;
  descriptionErrorMessage: string;
  handleNameChange: (value: ChangeEvent<HTMLInputElement>) => void;
  handleDescriptionChange: (value: ChangeEvent<HTMLInputElement>) => void;
  handleVisibilityChange: (event: SelectChangeEvent) => void;
  handleDescriptionBlur: () => void;
  isOpen: boolean;
  showUseOrgDefaults: boolean;
  useOrgDefaults: boolean;
  handleUseOrgDefaultsChange: (event: ChangeEvent<HTMLInputElement>) => void;
  orgDefaultsPreview: OrgDefaultsPreview;
}

const DEFAULT_CAPTION =
  "Pre-fills SAM (industries, geographies), SOM (company size, products), and Competitors from your Organization Preferences. TAM is unaffected.";

function OrgDefaultsCaption({ preview }: { preview: OrgDefaultsPreview }) {
  if (preview.status === "loading") {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={12} />
        <FormHelperText sx={{ m: 0 }}>
          Loading your Organization Preferences…
        </FormHelperText>
      </Stack>
    );
  }

  if (preview.status === "error") {
    return (
      <FormHelperText error sx={{ ml: 0 }}>
        Couldn't load your Organization Preferences. Uncheck and check again to
        retry.
      </FormHelperText>
    );
  }

  if (preview.status === "ready") {
    if (preview.isEmpty) {
      return (
        <FormHelperText sx={{ ml: 0 }}>
          No Organization Preferences are configured. Ask an admin to set them
          in Organization Preferences.
        </FormHelperText>
      );
    }

    const resolvedGroups = groupedResolved(
      countResolved(preview.result),
      preview.result.resolved
    );
    const resolvedNameGroups = groupedResolvedNames(preview.result.resolved);
    const unresolvedGroups = groupedUnresolved(preview.result.unresolved);

    return (
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {resolvedGroups.length > 0 && (
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FormHelperText sx={{ m: 0 }}>
                <strong>Will be applied</strong>
              </FormHelperText>
              <TooltipIcon
                groups={resolvedNameGroups.map((g) => ({
                  group: g.group,
                  label: g.label,
                  sections: g.sections,
                }))}
                testId="resolved-defaults-tooltip-icon"
              />
            </Stack>
            {resolvedGroups.map((g) => (
              <FormHelperText key={g.group} sx={{ m: 0, ml: 1 }}>
                <strong>{g.label}:</strong> {g.summary}.
              </FormHelperText>
            ))}
          </Box>
        )}
        {unresolvedGroups.length > 0 && (
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FormHelperText sx={{ m: 0 }}>
                <strong>Won't be applied (unrecognized in HG)</strong>
              </FormHelperText>
              <TooltipIcon
                groups={unresolvedGroups.map((g) => ({
                  group: g.group,
                  label: g.label,
                  sections: g.sections,
                }))}
                footer="Review them in Organization Preferences."
                testId="unresolved-defaults-tooltip-icon"
              />
            </Stack>
            {unresolvedGroups.map((g) => (
              <FormHelperText key={g.group} sx={{ m: 0, ml: 1 }}>
                <strong>{g.label}:</strong>{" "}
                {g.sections
                  .map((section) => {
                    if (section.label === "Employees Range") {
                      return `Unrecognized employees range: ${section.items.join(
                        ", "
                      )}`;
                    }
                    if (section.label === g.label)
                      return String(section.items.length);
                    return `${section.items.length} ${pluralizeSectionLabel(
                      section.label,
                      section.items.length
                    )}`;
                  })
                  .join(", ")}
                .
              </FormHelperText>
            ))}
          </Box>
        )}
      </Stack>
    );
  }

  // idle — checkbox not yet checked
  return <FormHelperText sx={{ ml: 0 }}>{DEFAULT_CAPTION}</FormHelperText>;
}

interface TooltipGroup {
  group: string;
  label: string;
  sections: UnresolvedSection[];
}

function TooltipIcon({
  groups,
  footer,
  testId,
}: {
  groups: TooltipGroup[];
  footer?: string;
  testId: string;
}) {
  const content = (
    <Stack spacing={1} sx={{ py: 0.5 }}>
      {groups.map((g) => {
        const onlySection = g.sections.length === 1 ? g.sections[0] : null;
        const sectionMatchesGroup =
          onlySection?.label.toLowerCase() === g.label.toLowerCase();

        return (
          <Box key={g.group}>
            <Typography variant="caption" fontWeight={600}>
              {g.label}
            </Typography>
            {onlySection && sectionMatchesGroup ? (
              <Typography variant="caption" component="div" sx={{ ml: 1 }}>
                {onlySection.items.join(", ")}
              </Typography>
            ) : (
              g.sections.map((section) => (
                <Typography
                  key={section.label}
                  variant="caption"
                  component="div"
                  sx={{ ml: 1 }}
                >
                  <strong>{section.label}:</strong> {section.items.join(", ")}
                </Typography>
              ))
            )}
          </Box>
        );
      })}
      {footer && (
        <Typography variant="caption" fontStyle="italic">
          {footer}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Tooltip
      title={content}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "text.primary",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 2,
            maxWidth: 360,
            wordBreak: "break-word",
          },
        },
        arrow: {
          sx: {
            color: "#fff",
            "&::before": {
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "#fff",
              boxSizing: "border-box",
            },
          },
        },
      }}
    >
      <HelpOutlineIcon
        fontSize="inherit"
        data-testid={testId}
        sx={{ cursor: "help", color: "text.secondary" }}
      />
    </Tooltip>
  );
}

export function WorkspaceDialogContent(props: Props) {
  return (
    <DialogContent id={`${props.baseId}-dialog-content`}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" padding="100px 165px 65px 125px">
          <img src={workspaceIcon} alt="workspace-icon" />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap="25px"
          width="400px"
          padding="10px 65px 0px 0px"
        >
          <UserFormTextField
            id={`${props.baseId}-name-input`}
            label="Project Name"
            name="workspaceName"
            value={props.workspaceName}
            onChange={props.handleNameChange}
            error={props.isNameInError}
            errorMessage={props.nameErrorMessage}
            maxLength={50}
            autoFocus={props.isOpen}
            selectAllOnInitialFocus={true}
          />
          <FormControl
            variant="standard"
            fullWidth
            sx={{
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#343c4399",
              },
              "& .MuiInput-underline:before": {
                borderBottomWidth: "1px !important",
                borderBottomColor: "#343c4399",
              },
              "& .MuiInput-underline:after": {
                borderBottomWidth: "1px !important",
                borderBottomColor: "#343c4399",
              },
              "& .MuiSelect-icon": {
                color: "#343c4399 !important",
              },
            }}
          >
            <InputLabel
              id="inputLabel"
              sx={{
                color: "#343c4399",
              }}
            >
              Visibility
            </InputLabel>
            <Select<string>
              data-testid="workspace-dialog-visibility-select"
              labelId="inputLabel"
              value={props.workspaceVisibility}
              onChange={props.handleVisibilityChange}
              label="Visibility"
            >
              <MenuItem value={WorkspaceVisibility.PRIVATE}>Private</MenuItem>
              <MenuItem value={WorkspaceVisibility.PUBLIC}>Public</MenuItem>
            </Select>
          </FormControl>
          <UserFormTextField
            id={`${props.baseId}-description-input`}
            label="Description"
            name="description"
            value={props.workspaceDescription}
            onChange={props.handleDescriptionChange}
            onBlur={props.handleDescriptionBlur}
            error={props.isDescriptionInError}
            errorMessage={props.descriptionErrorMessage}
          />
          {props.showUseOrgDefaults && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.useOrgDefaults}
                    onChange={props.handleUseOrgDefaultsChange}
                    disabled={
                      props.orgDefaultsPreview.status === "ready" &&
                      props.orgDefaultsPreview.isEmpty
                    }
                    data-testid="create-workspace-use-org-defaults-btn"
                  />
                }
                label="Use Organization Preferences"
              />
              <OrgDefaultsCaption preview={props.orgDefaultsPreview} />
            </Box>
          )}
        </Box>
      </Box>
    </DialogContent>
  );
}
